import {DEFAULT_LABEL_STYLE} from './Theme';

/**
 * Given a number of values for a discrete axis and the size of that axis, calculate how many values we should
 * show on the axis.
 *
 * Ideally we want to have a tick label for every row/column on the axis, but there might be so many that
 * even with a 90 degree rotation they would render over each other. In that case, we have to show less ticks,
 * so this function calculates the ideal number of values to show.
 *
 * The algorithm is an approximation and its success was mostly decided because its result "looks good". It works
 * on the fact that if tick labels are maximally rotated (i.e. 90 degrees), their widths on the axis is the font
 * height/size. Since we do this calculation before calculating the tick rotation (because rotation depends on
 * #ticks), we just assume that the labels will be rotated maximally to fit the given space, so we just need to
 * make sure that the number of values on the axis at least fits in that case.
 *
 * The algorithm does the following:
 *    1. Is numTicks * fontSize * 1.1 greater then the axisSize?
 *        - The 1.1 multiplication is added because it looked nicer
 *    2. Yes: divide numTicks by 2, repeat step 1
 *    3. No: return numTicks
 *
 * @param numValues The number of values in the domain of the axis.
 * @param axisSize The size of the axis (e.g. `chartWidth` for the x-axis).
 * @returns Number of values/ticks we should show on the axis.
 */
export default function getDiscreteAxisNumTicks(numValues: number, axisSize: number): number {
  let numTicks = numValues;

  while (numTicks * DEFAULT_LABEL_STYLE.fontSize * 1.1 > axisSize && numTicks > 1) {
    numTicks = Math.floor(numTicks / 2);
  }

  return numTicks;
}
